import React from 'react';
import { BASE_URL } from '../Api';
import { Helmet, HelmetProvider } from "react-helmet-async";
import FreeHighlight from '../partials/FreeHighlight';

const MrpPlugin = () => {
  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Maximum retail price (MRP) - Aronium</title>
          <meta name="description" content="Specify and print products' maximum retail price on receipts and invoices, along with customer total savings" />
        </Helmet>
      </HelmetProvider>

      <section className="py-8 max-w-screen-xl px-4 mx-auto">
        <div className="max-w-screen-xl mx-auto text-center">
          <h1 className="text-4xl mb-4">
            Maximum retail price (MRP)
          </h1>
          <p>
            Specify and print a product's maximum retail price on receipts and invoices, along with customer total savings.
          </p>

          {/* Call to Action Buttons */}
          <div className="mt-8 sm:mt-12 flex flex-col sm:flex-row justify-center gap-4 sm:gap-6">
            {/* Download for Aronium Lite Button */}
            <a
              href={`${BASE_URL}/plugins/mrp/latest/Aronium.Pos.Mrp.Lite.exe`}
              className="text-white inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-full bg-pink-700 hover:bg-pink-800 transition duration-300 shadow-lg focus:ring-4 focus:ring-pink-600 focus:outline-none hover:no-underline"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                />
              </svg>
              Download for Aronium Lite
            </a>

            {/* Download for Aronium Pro Button */}
            <a
              href={`${BASE_URL}/plugins/mrp/latest/Aronium.Pos.Mrp.Pro.exe`}
              className="text-white inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-full bg-rose-600 hover:bg-rose-700  transition duration-300 shadow-lg focus:ring-4 focus:ring-blue-300 focus:outline-none hover:no-underline"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                />
              </svg>
              Download for Aronium Pro
            </a>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <div className="flex flex-col text-center mt-10">
        <div className="grid place-content-center justify-items-center px-4">
          <img src="/images/mrp.png" className='xl:max-w-6xl border-t-8 border-l border-r rounded-t-xl border-solid border-red-500' alt="Setup email reporting" />
        </div>
      </div>
      <div className="bg-gray-200 py-20">
        <div className="relative max-w-7xl mx-auto text-center px-8 lg:px-12">
          <h2 className="text-5xl font-bol text-gray-800 leading-tight mb-8">
            Why use <span className='font-semibold'>MRP</span> plugin?
          </h2>
          <p className="text-lg text-gray-600mx-auto">
            The <span className='font-semibold'>Maximum Retail Price (MRP)</span> plugin enables businesses to assign a maximum retail price to products and display this information on receipts and invoices. This feature is particularly useful in regions where displaying MRP is a legal requirement, ensuring compliance and transparency with customers.
          </p>
        </div>
        <FreeHighlight className="mt-12" />
      </div>

      {/* Key Features Section */}
      <div className="py-24">
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
          <div className="bg-white p-8 shadow-lg rounded-lg border border-gray-200">
            <h3 className="text-2xl font-semibold text-gray-800">Assign MRP to Products</h3>
            <p className="mt-4 text-gray-600">
              After installing the plugin, you can add an MRP value to each product's details. If a product's MRP is set to zero, it will not be printed on receipts or invoices.
            </p>
          </div>
          <div className="bg-white p-8 shadow-lg rounded-lg border border-gray-200">
            <h3 className="text-2xl font-semibold text-gray-800">Automatic MRP Printing</h3>
            <p className="mt-4 text-gray-600">
              Once MRP is assigned, it will automatically appear on receipts and invoices. Additionally, customer savings based on MRP will be calculated and displayed, providing clear information on discounts received.
            </p>
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="bg-gradient-to-tr from-rose-700 to-rose-800 py-24 text-center text-gray-700">
        <div className="mx-auto px-4 text-white">
          <h2 className="text-4xl xl:text-5xl mb-6">
            Ready to start displaying maximum retail prices on your sales documents?
          </h2>
          <p className="text-xl mb-10">
            Ensure compliance and enhance customer transparency by integrating the MRP plugin into your Aronium POS system.
          </p>
          <a
            href="https://help.aronium.com/hc/en-us/articles/10917321098898-Using-maximum-retail-price"
            className="button-rounded-white px-12 py-4"
            target="_blank"
            rel="noreferrer">
            Learn more <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="ml-2 w-6 h-6 transition-all">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3" />
            </svg>
          </a>
        </div>
      </div>
    </>
  );
};

export default MrpPlugin;
